import React, { useState, useEffect } from 'react';
import { List, Avatar, Spin, Empty, Typography, Tag } from 'antd';
import { ShopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getVendors } from '@/api/vendors';
import { Vendor } from '@/types/resources/vendor';

const { Text } = Typography;

interface VendorListTabProps {
  onSelect: (vendor: Vendor) => void;
  searchTerm: string;
}

export const VendorListTab: React.FC<VendorListTabProps> = ({ onSelect, searchTerm }) => {
  const { t } = useTranslation('vendors');
  const { firestoreUser } = useAuth();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load vendors
  useEffect(() => {
    if (!firestoreUser?.workspace?.id) return;

    const loadVendors = async () => {
      try {
        setIsLoading(true);
        const vendorsData = await getVendors({
          firestoreUser
        });
        setVendors(vendorsData.vendors);
      } catch (error) {
        console.error('Error loading vendors:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadVendors();
  }, [firestoreUser?.workspace?.id]);

  // Filter vendors based on search term
  useEffect(() => {
    if (!vendors || !Array.isArray(vendors)) {
      setFilteredVendors([]);
      return;
    }
    
    if (!searchTerm.trim()) {
      setFilteredVendors(vendors);
    } else {
      const filtered = vendors.filter(vendor =>
        vendor?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor?.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (vendor?.services && vendor.services.some(service => 
          service.toLowerCase().includes(searchTerm.toLowerCase())
        ))
      );
      setFilteredVendors(filtered);
    }
  }, [vendors, searchTerm]);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">{t('loading', 'Loading vendors...')}</Text>
        </div>
      </div>
    );
  }

  if (filteredVendors.length === 0) {
    return (
      <Empty
        description={
          searchTerm.trim() 
            ? t('noVendorsFound', 'No vendors found matching your search')
            : t('noVendors', 'No vendors found in this workspace')
        }
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <List
      itemLayout="horizontal"
      dataSource={filteredVendors}
      style={{ maxHeight: '400px', overflowY: 'auto' }}
      renderItem={(vendor) => {
        if (!vendor) return null;
        
        return (
          <List.Item
            onClick={() => onSelect(vendor)}
          style={{
            padding: '12px 16px',
            cursor: 'pointer',
            borderBottom: '1px solid #f0f0f0',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <List.Item.Meta
            avatar={
              <Avatar 
                icon={<ShopOutlined />}
                size="large"
              />
            }
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text strong style={{ fontSize: '16px' }}>
                  {vendor.name}
                </Text>
                {vendor.status && (
                  <Tag 
                    color={vendor.status === 'active' ? 'green' : 'red'}
                  >
                    {vendor.status}
                  </Tag>
                )}
              </div>
            }
            description={
              <div>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  👤 {vendor.contactPerson}
                </Text>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    📧 {vendor.email || 'No email'}
                  </Text>
                </div>
                {vendor.phone && (
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      📞 {vendor.phone}
                    </Text>
                  </div>
                )}
                {vendor.services && vendor.services.length > 0 && (
                  <div style={{ marginTop: '4px' }}>
                    {vendor.services.slice(0, 3).map((service, index) => (
                      <Tag key={index} style={{ marginRight: '4px' }}>
                        {service}
                      </Tag>
                    ))}
                    {vendor.services.length > 3 && (
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        +{vendor.services.length - 3} more
                      </Text>
                    )}
                  </div>
                )}
              </div>
            }
          />
        </List.Item>
        );
      }}
    />
  );
};
